//@author: devin

import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    input,
    Input,
    model,
    NgZone,
    OnInit,
    output,
    Output,
    TemplateRef,
    viewChild,
} from "@angular/core";
import { PlacementArray } from "@ng-bootstrap/ng-bootstrap";
import { PopoverComponent } from "../s25-popover/popover.component";
import { MultiselectModelI } from "./s25.multiselect.component";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25MultiselectResultsComponent } from "./s25.multiselect.results.component";
import { S25ItemI } from "../../pojo/S25ItemI";
import { InferSignalProp } from "../../pojo/Util";

@TypeManagerDecorator("s25-ng-multiselect-popup")
@Component({
    selector: "s25-ng-multiselect-popup",
    template: `
        @if (!readonly()) {
            <div class="s25-multiselect-popup-position-header">
                @if (init) {
                    <s25-popover
                        class="s25-multiselect-popup-placeholder"
                        [modelBean]="modelBean"
                        [popoverTemplate]="popoverTemplate || defaultPopoverTemplate"
                        [openTrigger]="openTrigger"
                        [onBody]="popoverOnBody"
                        [closeTrigger]="closeTrigger"
                        [placement]="popoverPlacement"
                        [disabled]="disabled"
                        [triggerTabIndex]="-1"
                        (shown)="onOpen()"
                        (hidden)="onClose()"
                    >
                        @if (!this.modelBean.textButton) {
                            <button
                                class="aw-button {{ modelBean.buttonClass }} multiSelectButton"
                                [disabled]="disabled"
                                [attr.aria-label]="ariaLabel() || null"
                                [attr.aria-required]="ariaRequired() ? 'true' : null"
                                [attr.aria-invalid]="ariaInvalid() ? 'true' : null"
                                [attr.aria-describedby]="ariaDescribedby() || null"
                            >
                                {{ modelBean.buttonText }}
                            </button>
                        }
                        @if (modelBean.textButton) {
                            <button
                                class="c-textButton"
                                [disabled]="disabled"
                                [attr.aria-label]="ariaLabel() || null"
                                [attr.aria-required]="ariaRequired() ? 'true' : null"
                                [attr.aria-invalid]="ariaInvalid() ? 'true' : null"
                                [attr.aria-describedby]="ariaDescribedby() || null"
                            >
                                {{ modelBean.title }} +
                            </button>
                        }
                    </s25-popover>
                }
            </div>
        }
        <ng-container
            [ngTemplateOutlet]="resultsTemplate || multiselectResults"
            [ngTemplateOutletContext]="{ modelBean: modelBean }"
        ></ng-container>

        <ng-template #multiselectResults let-modelBean="modelBean">
            @if (init && modelBean.showResult) {
                <s25-ng-multiselect-results
                    [showMatching]="modelBean.showMatching"
                    [matching]="modelBean.matching ?? 'any'"
                    (matchingChange)="modelBean.matching = $event; modelBean.onMatchingChange($event)"
                    [hasQuantity]="modelBean.hasQuantity"
                    [uuid]="modelBean.uuid"
                    [(selectedItems)]="value"
                    [disabled]="disabled || readonly()"
                    [sortBy]="sortResultsBy()"
                    [sortOrder]="sortResultsOrder()"
                    (clear)="modelBean.selectNone()"
                    (select)="modelBean.addSelectedItem($event)"
                    (remove)="modelBean.removeSelectedItem($event); modelBean.onChange?.(); changed.emit()"
                    (changed)="modelBean.onChange?.()"
                />
            }
        </ng-template>

        <ng-template #defaultPopoverTemplate>
            <s25-ng-multiselect
                [modelBean]="modelBean"
                [value]="value() ?? []"
                (valueChange)="value.set($event)"
            ></s25-ng-multiselect>
        </ng-template>
    `,
    styles: `
        :host {
            display: inline-block;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25MultiselectPopupComponent implements OnInit, AfterViewInit {
    @Input() modelBean: MultiselectModelI;
    @Input() trigger: "hover" | "click" = "click";
    @Input() popoverTemplate: TemplateRef<any>;
    @Input() popoverPlacement: PlacementArray = "auto";
    @Input() popoverOnBody = false;
    @Input() customPopoverClass?: string;
    @Input() resultsTemplate: TemplateRef<unknown>;
    @Input() disabled: boolean = false;
    readonly readonly = input(false);
    readonly ariaLabel = input<string | undefined>(undefined);
    readonly ariaRequired = input(false);
    readonly ariaInvalid = input(false);
    readonly ariaDescribedby = input<string | undefined>(undefined);
    readonly value = model<S25ItemI[]>();
    readonly sortResultsBy = input<InferSignalProp<typeof S25MultiselectResultsComponent, "sortBy">>("none");
    readonly sortResultsOrder = input<InferSignalProp<typeof S25MultiselectResultsComponent, "sortOrder">>("asc");

    @Output() close = new EventEmitter<void>();
    readonly changed = output<void>();

    readonly defaultPopoverTemplate = viewChild("defaultPopoverTemplate", { read: TemplateRef<void> });
    readonly popoverComponent = viewChild(PopoverComponent);
    readonly resultsComponent = viewChild(S25MultiselectResultsComponent);

    init: boolean = false;
    openTrigger: "mouseenter" | "click";
    closeTrigger: "mouseleave" | "click";

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
        private zone: NgZone,
    ) {
        this.elementRef.nativeElement.angBridge = this;
    }

    onOpen = () => {};

    onClose = () => {
        if (!this.modelBean.closing) {
            this.modelBean.cancel?.(true); //cancel but skip calling popup close since we are already closed here
        } else {
            this.modelBean.closing = false;
        }
        this.close.emit();
    };

    forceClose = () => {
        this.modelBean.closing = true;
        this.popoverComponent()?.forceClose();
    };

    ngOnInit() {
        this.openTrigger = this.trigger === "hover" ? "mouseenter" : "click";
        this.closeTrigger = this.trigger === "hover" ? "mouseleave" : "click";
    }

    ngAfterViewInit() {
        this.zone.run(() => {
            this.modelBean.popoverTemplate = this.popoverTemplate || this.defaultPopoverTemplate();
            this.modelBean.popoverClass = this.customPopoverClass ? this.customPopoverClass : "s25-multiselect-popup";
            this.modelBean.buttonText = this.modelBean.buttonText || `Select ${this.modelBean.title}`;
            this.modelBean.buttonClass = this.modelBean.buttonClass || "aw-button--outline";
            this.init = true;
            this.cd.detectChanges();
        });
    }

    refresh() {
        this.cd.detectChanges();
        this.resultsComponent()?.refresh();
    }
}
